/**
 * Currency + FX helpers (BW12 — Finance domain).
 *
 * Currencies are stored as ISO 4217 codes on every row. Display order is
 * locale-aware: CHF/EUR/JPY are post-symbol in en-CH, USD pre-symbol in en-US.
 * We default to a `pre-symbol + 2 decimals` format which Jonas approved as
 * "fine for now" — we can swap for `Intl.NumberFormat` per locale once we
 * know what reads better.
 */

export type CurrencyCode = string;

export const CURRENCY_SYMBOLS: Record<string, string> = {
  CHF: "CHF",
  EUR: "€",
  USD: "$",
  GBP: "£",
  JPY: "¥",
  SEK: "kr",
  DKK: "kr",
  NOK: "kr",
  AUD: "A$",
  CAD: "C$",
};

const DECIMALS: Record<string, number> = {
  JPY: 0,
};

export function symbolFor(code: string): string {
  return CURRENCY_SYMBOLS[code.toUpperCase()] ?? code;
}

export function formatMoney(
  amount: number | null | undefined,
  code: string,
  opts: { sign?: "auto" | "always" | "never" } = {},
): string {
  if (amount == null || Number.isNaN(amount)) return "—";
  const decimals = DECIMALS[code.toUpperCase()] ?? 2;
  const abs = Math.abs(amount).toFixed(decimals);
  const symbol = symbolFor(code);
  const signMode = opts.sign ?? "auto";
  const sign = amount < 0 ? "−" : signMode === "always" ? "+" : "";
  // Symbols that are more than 2 chars (CHF, kr, A$, C$) read better post-spaced.
  if (symbol.length >= 2 && /[A-Za-z]/.test(symbol)) {
    return `${sign}${abs} ${symbol}`;
  }
  return `${sign}${symbol}${abs}`;
}

/**
 * Convert one amount via a daily FX rate. Round to the destination
 * currency's decimals.
 */
export function convertAmount(
  amount: number,
  rate: number,
  toCurrency: string,
): number {
  const decimals = DECIMALS[toCurrency.toUpperCase()] ?? 2;
  return Number((amount * rate).toFixed(decimals));
}

/**
 * Frankfurter API: ECB-rate based, no API key.
 * Pulls latest rate (or specific date) for a single base→quote pair.
 *
 * Caller is responsible for caching into `fx_rate_daily`.
 */
export async function fetchFrankfurterRate(
  base: string,
  quote: string,
  date?: string, // YYYY-MM-DD; omit for "latest"
): Promise<number | null> {
  if (base === quote) return 1;
  const path = date ? date : "latest";
  const url = `https://api.frankfurter.dev/v1/${path}?base=${encodeURIComponent(base)}&symbols=${encodeURIComponent(quote)}`;
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const data = (await res.json()) as { rates?: Record<string, number> };
    const rate = data.rates?.[quote];
    return typeof rate === "number" ? rate : null;
  } catch {
    return null;
  }
}
